import type { AttributeItem } from '~/components/common/attribute-score-list';
import type { RD3CItem } from '~/components/common/rd3c';
import type {
  AttributeScore,
  CategoryScore,
  CategoryScoreSnapshot,
  DimensionScore,
} from '~/features/trial/types/trial.types';

/**
 * Fixed weighting percentages for each of the five RD3C scoring dimensions, in display order.
 * Moved here from mock data so the weights remain canonical regardless of API score availability.
 * Exported so non-React surfaces (e.g. the Feed Audit HTML export) share the same
 * canonical order + weights and never drift from the in-app dimension table.
 */
export const DIMENSION_CONFIG: { key: string; percentage: string }[] = [
  { key: 'correctness', percentage: '30%' },
  { key: 'completeness', percentage: '25%' },
  { key: 'compliance', percentage: '25%' },
  { key: 'relevance', percentage: '10%' },
  { key: 'differentiation', percentage: '10%' },
];

/**
 * Transforms API `attributeScores` into `AttributeItem` entries for the `AttributeScoreList` component.
 * Maps `attribute` → `title`, `before`/`after` → `beforeScore`/`afterScore`.
 */
export function transformAttributeScoresToItems(
  attributeScores: AttributeScore[],
  maxScore = 10,
): AttributeItem[] {
  return attributeScores.map(({ attribute, before, after }) => ({
    title: attribute,
    // API can return null even though the type says number; guard so toFixed never throws.
    beforeScore: before ?? 0,
    afterScore: after ?? 0,
    maxScore,
  }));
}

/**
 * Transforms API `categoryScores` into `AttributeItem` entries for the `AttributeScoreList` component.
 * Maps `category` → `title`, `before`/`after` → `beforeScore`/`afterScore`.
 */
export function transformCategoryScoresToItems(
  categoryScores: CategoryScore[],
  maxScore = 10,
): AttributeItem[] {
  return categoryScores.map(({ category, before, after }) => ({
    title: category,
    // API can return null even though the type says number; guard so toFixed never throws.
    beforeScore: before ?? 0,
    afterScore: after ?? 0,
    maxScore,
  }));
}

/**
 * Transforms API `dimensionScores` into `RD3CItem` entries for the `RD3C` component.
 * Merges API before/after scores with fixed dimension weights and pre-translated labels.
 * Dimensions are matched by key; falls back to 0 when no API score is found for a dimension.
 */
export function transformDimensionScoresToRD3CItems(
  dimensionScores: DimensionScore[],
  labels: { title: string; description: string }[],
): RD3CItem[] {
  const scoreByDimension = new Map(dimensionScores.map((s) => [s.dimension, s]));

  return DIMENSION_CONFIG.map(({ key, percentage }, index) => {
    const score = scoreByDimension.get(key);
    const description = labels[index]?.description;
    return {
      percentage,
      title: labels[index]?.title ?? key,
      ...(description !== undefined && { description }),
      beforeScore: score?.before ?? 0,
      afterScore: score?.after ?? 0,
    };
  });
}

/**
 * Merges two flat attribute-score maps (e.g. `contentAttributeScores` / `contentAttributeScoresBefore`)
 * keyed by the same camelCase field name into `AttributeItem` entries, title-casing the key.
 */
export function transformAttributeScoreMapsToItems(
  after: Record<string, number | null>,
  before: Record<string, number | null>,
  maxScore = 10,
): AttributeItem[] {
  return Object.entries(after)
    .map(([key, value]) => ({
      title: key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()),
      beforeScore: before[key] ?? 0,
      afterScore: value ?? 0,
      maxScore,
    }))
    .sort((a, b) => b.afterScore - a.afterScore); // sort descending by afterScore
}

/**
 * Merges two category-score snapshot arrays (e.g. `categoryScores` / `categoryScoresBefore`)
 * matched by `category` into `AttributeItem` entries. `maxScore` declares the scale the raw
 * scores are already on (e.g. 10 or 100) so `AttributeScoreList` can render bars correctly.
 */
export function transformCategoryScoreSnapshotsToItems(
  after: CategoryScoreSnapshot[],
  before: CategoryScoreSnapshot[],
  maxScore = 10,
): AttributeItem[] {
  const beforeByCategory = new Map(before.map((c) => [c.category, c.score]));

  return after
    .map(({ category, score }) => ({
      title: category,
      beforeScore: beforeByCategory.get(category) ?? 0,
      afterScore: score,
      maxScore,
    }))
    .sort((a, b) => b.afterScore - a.afterScore); // sort descending by afterScore
}

/** Panel-row labels hidden from the content-attribute score chart, matched
 *  case-insensitively. Google Product Category is a taxonomy mapping rather than
 *  a scored content attribute, so it only ever shows 0/10 noise — omit it. */
const HIDDEN_QUALITY_SCORE_LABELS = new Set(['google product category']);

/**
 * Transforms quality-score panel rows into `AttributeItem` entries for `AttributeScoreList`.
 * `maxScore` declares the scale the raw score is already on (e.g. 10 or 100).
 * Skips non-scored taxonomy rows (see `HIDDEN_QUALITY_SCORE_LABELS`).
 *
 * Rows with a null after score are dropped: the agent grades a wider attribute
 * set before enrichment than after (POST only re-grades what enrichment
 * touched), so the panel legitimately carries attributes with a before and no
 * after. Charting those as zero would draw phantom bars for work that was never
 * measured, so an unscored after side is omitted rather than coerced.
 */
export function transformQualityScorePanelToItems(
  rows: { label: string; beforeScore: number | null; score: number | null }[],
  maxScore = 10,
): AttributeItem[] {
  return rows
    .filter(({ label }) => !HIDDEN_QUALITY_SCORE_LABELS.has(label.trim().toLowerCase()))
    .filter((row): row is typeof row & { score: number } => row.score !== null)
    .map(({ label, beforeScore, score }) => ({
      title: label,
      // `AttributeScoreList` has no representation for an absent baseline, so a
      // null before is drawn as an empty bar. Safe in practice: PRE grades a
      // superset of POST, so a row with an after score effectively always has a
      // before. Revisit if the list gains a true "not graded" state.
      beforeScore: beforeScore ?? 0,
      afterScore: score,
      maxScore,
    }));
}
